import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";

import DriverRequestDetails from "@/components/Driver/DriverRequestDetails";
import { ConfirmationDialogButton } from "@/components/Driver/DriverRequestDetails/confirmation-dialog-button";
import {
  useConfirmDriverMutation,
  useGetDriverByIdQuery,
  useRejectDriverRequestMutation,
} from "@/libs/generated/hooks";
import {
  DRIVER_ACTIONS,
  ERROR_MESSAGES,
  NAVIGATION_ROUTES,
  SUCCESS_MESSAGES,
  TOAST_CONFIG,
} from "@/utils/constants/driver-actions";

function PendingDriverDetails() {
  const { id } = useParams();
  const { t } = useTranslation();
  const theme = useTheme();

  const navigate = useNavigate();
  if (!id) throw new Error("no driver id in params");
  const {
    data,
    error,
    isPending: loading,
  } = useGetDriverByIdQuery({
    GetByIdInput: {
      id: Number(id),
    },
  });
  const confirmDriver = useConfirmDriverMutation({
    onSuccess: () => {
      toast.success(t(SUCCESS_MESSAGES.CONFIRM), TOAST_CONFIG);
      navigate(NAVIGATION_ROUTES.ACCEPTED_REQUESTS);
    },
    onError: (_err: any) => {
      toast.error(t(ERROR_MESSAGES.GENERIC), TOAST_CONFIG);
    },
  });
  const rejectDriver = useRejectDriverRequestMutation({
    onSuccess: () => {
      toast.success(t(SUCCESS_MESSAGES.REJECT), TOAST_CONFIG);
      navigate(NAVIGATION_ROUTES.REJECTED_REQUESTS);
    },
    onError: (_err: any) => {
      toast.error(t(ERROR_MESSAGES.REJECT), TOAST_CONFIG);
    },
  });

  const isConfirmingDriver = confirmDriver.isPending;
  const isRejectingDriver = rejectDriver.isPending;

  if (loading) return <p>{t("common.loading")}</p>;

  if (error) {
    return <p>{t("common.error.generic")}</p>;
  }
  if (!data?.getDriverById) {
    return <p>{t("common.no-data")}</p>;
  }
  const driverData = data.getDriverById;

  return (
    <DriverRequestDetails
      driverData={driverData}
      showTerms={true}
      actions={
        <Box sx={{ display: "flex", gap: 2, width: "100%", justifyContent: "flex-end" }}>
          <ConfirmationDialogButton
            buttonText={DRIVER_ACTIONS.CONFIRM.buttonText}
            title={DRIVER_ACTIONS.CONFIRM.title}
            description={DRIVER_ACTIONS.CONFIRM.confirmation}
            buttonColor={theme.palette.success.main}
            icon={DRIVER_ACTIONS.CONFIRM.icon}
            actionIsLoading={isConfirmingDriver}
            onConfirm={() => {
              confirmDriver.mutate({ input: { driverId: driverData.id } });
            }}
          />
          <ConfirmationDialogButton
            buttonText={DRIVER_ACTIONS.REJECT.buttonText}
            title={DRIVER_ACTIONS.REJECT.title}
            buttonColor={theme.palette.error.main}
            description={DRIVER_ACTIONS.REJECT.confirmation}
            icon={DRIVER_ACTIONS.REJECT.icon}
            actionIsLoading={isRejectingDriver}
            onConfirm={() => {
              rejectDriver.mutate({ input: { email: driverData.email } });
            }}
          />
        </Box>
      }
    />
  );
}

export default PendingDriverDetails;
